import { Alert, Button, Col, Form, Input, Tooltip } from "antd";
import TextArea from "antd/lib/input/TextArea";
import { DatePicker as DatePickerJalali } from "antd-jalali";
import { ArrowLeftOutlined, StopOutlined, CopyOutlined } from '@ant-design/icons';
import { Core } from "../../components/infrastructure/core";
import { enmEntityState, ICallOptions, IEntity, IResultSet } from "../../components/infrastructure/models";
import { EvaluationComponent, IEvaluationComponentFormProps, IEvaluationComponentProps, IEvaluationComponentStates, IEvaluationInitialData } from "./evaluationComponent";
import { IAttachment } from "../../baseInfo/attachment/interfaces";
import { AttachmentDownloader } from "../../baseInfo/attachment/attachmentDownloader";
import { IPolicyholderConfirmation } from "./policyholderConfirmation";
import React from "react";
import dayjs from 'dayjs';
import { enmEvaluationMethods, evaluationMethodsOptions } from "../../baseInfo/damageCase/interfaces";
import HelperPopover from "../../components/helperPopover";

type LayoutType = Parameters<typeof Form>[0]['layout'];

export interface IContactSetting extends IEntity {
    DamageCaseEvaluatorId: number;
    Description?: string | null;
    ConferenceUrl?: string | null;
    ContactDate?: any;
}

export interface IContactSettingStates extends IEvaluationComponentStates<IContactSetting> {
    policyholderConfirmation?: IPolicyholderConfirmation;
    previousMakingContactAttachments: IAttachment[];
}

export interface IContactSettingProps extends IEvaluationComponentProps {
    
}

interface IContactSettingInitialData extends IEvaluationInitialData {
    PolicyholderConfirmation?: IPolicyholderConfirmation;
    PreviousMakingContactAttachments: IAttachment[];
}

export interface IContactSettingFormProps extends IEvaluationComponentFormProps<IContactSetting> {
    showPolicyholderMessage?: boolean;
    onChangeDescription?: (value?: any) => void;
    onChangeConferenceUrl?: (value?: any) => void;
    onChangeContactDate?: (e: any) => void;
    policyholderConfirmation?: IPolicyholderConfirmation;
    previousMakingContactAttachments: IAttachment[];
    evaluationMethodChanged?: boolean;
}

const sampleDescriptions = [
    { type: enmEvaluationMethods.VideoCall, message: "لطفا در زمان مقرر در جلسه آنلاین حاضر شوید" },
    //{ type: enmEvaluationMethods.EvaluatorInPerson, message: "در زمان مقرر ارزیاب به شما مراجعه خواهد نمود، لطفا اصل دستگاه، اقلام موجود و مدارک شناسایی را به همراه داشته باشید" },
    { type: enmEvaluationMethods.PolicyholderInPerson, message: "در زمان مقرر با همراه داشتن دستگاه، اقلام موجود و اصل مدارک شناسایی به ارزیاب مربوطه مراجعه نمایید" },
    //{ type: enmEvaluationMethods.PolicyholderInPersonToInsurerBranch, message: "در زمان مقرر با همراه داشتن دستگاه، اقلام موجود و اصل مدارک شناسایی به شعبه مربوطه مراجعه نمایید" }
];

export const ContactSettingForm = (props: IContactSettingFormProps) => {
    const { EvaluationMethod } = props.damageCase;
    const formItemLayout = {
        labelCol: { xxl: 6, xl: 6, lg: 8, md: 8, sm: 8, xs: 8 },
        wrapperCol: { xxl: 12, xl: 12, lg: 16, md: 16, sm: 16, xs: 16 }
    };

    const [formLayout] = React.useState<LayoutType>('horizontal');
    const [form] = Form.useForm();

    React.useEffect(() => {
        let currentEntity = props.currentEntity;
        form.setFieldsValue(currentEntity);
    }, [props, form]);

    const methodTypeTitle = evaluationMethodsOptions.find(m => (props.evaluationMethodChanged && m.value === enmEvaluationMethods.VideoCall) || m.value === EvaluationMethod)?.makingContactTitle;

    const descriptionPopoverContent =
        <div>
            {sampleDescriptions.filter(a => a.type === EvaluationMethod).map(a =>
                <p
                    key={a.message}
                    className="helperDescription"
                    onClick={() => {
                        const desc = form.getFieldValue('Description');
                        const value = (desc ? desc + '\n' : '') + a.message;
                        props.onChangeDescription && props.onChangeDescription(value);
                    }} >
                    {a.message}
                </p>)}
        </div>;

    //const evaluator = props.damageCase.DamageCaseEvaluators.find(e => e.Id === props.currentEntity.DamageCaseEvaluatorId)?.Evaluator;

    return (
        <Form
            scrollToFirstError
            form={form}
            {...formItemLayout}
            layout={formLayout}
            onFinish={async (values) => {
                props.onSubmit && await props.onSubmit(values);
            }}
            onReset={() => {
                props.onClose && props.onClose(this);
            }}
        >
            {props.policyholderConfirmation
                ?
                <Form.Item
                    label={'نظر بیمه گزار/مشتری'}
                    name={'PolicyholderConfirmation'}
                    key={'PolicyholderConfirmation'}
                >
                    <p>{props.policyholderConfirmation.ObjectionDescription}</p>
                </Form.Item>
                : <></>
            }

            {props.previousMakingContactAttachments && props.previousMakingContactAttachments.length
                ? <Form.Item
                    label={'مستندات تماس توسط ارزیاب قبلی'}
                    name={'PreviousMakingContactAttachments'}
                    key={'PreviousMakingContactAttachments'}
                >
                    <AttachmentDownloader attachments={props.previousMakingContactAttachments} />
                </Form.Item>
                : <></>
            }

            <Form.Item
                label={'توضیحات'}
                name={'Description'}
                key={'Description'}
                rules={[{ required: true, message: 'توضیحات اجباری است' }]}
                extra={
                    props.disabled ? <></> : <HelperPopover descriptionPopoverContent={descriptionPopoverContent} title="نمونه توضیحات" placement="right" style={{ marginRight: 1, verticalAlign: 'top', position: "absolute", top: 0, left: 0 }} />
                }
            >
                <TextArea
                    onChange={e => props.onChangeDescription && props.onChangeDescription(e.target.value)}
                    disabled={props.disabled}
                    style={{ width: props.disabled ? "100%" : "calc(100% - 34px)" }} />
            </Form.Item>

            {EvaluationMethod === enmEvaluationMethods.VideoCall &&
                <>
                    <Form.Item
                        label={'لینک تماس تصویری'}
                        name={'ConferenceUrl'}
                        key={'ConferenceUrl'}
                        rules={[{ required: EvaluationMethod === enmEvaluationMethods.VideoCall, message: 'لینک تماس تصویری اجباری است' }]}
                        extra={
                            props.currentEntity.State === enmEntityState.Added && <Alert type="info" style={{ marginTop: 5 }} message="ارزیاب گرامی، درصورت لزوم می توانید لینک مورد نظر خود را در کادر بالا وارد نمایید" />
                        }
                    >
                        <Input
                            style={{ direction: "ltr" }}
                            onChange={e => props.onChangeConferenceUrl && props.onChangeConferenceUrl(e.target.value)}
                            disabled={props.disabled}
                            addonBefore={
                                <Tooltip title="کپی لینک" >
                                    <CopyOutlined onClick={async () => {
                                        if (props.currentEntity.ConferenceUrl)
                                            await navigator.clipboard.writeText(props.currentEntity.ConferenceUrl);
                                    }} />
                                </Tooltip>
                            } />

                    </Form.Item>
                </>
            }

            <Col xxl={18} xl={18} lg={24} md={24} sm={24} xs={24} style={{ marginRight: 10, marginTop: 5 }}>
                <Alert type="warning" message={
                    <Form.Item
                        label={'زمان ' + methodTypeTitle}
                        name={'ContactDateP'}
                        labelCol={{ xxl: 12, xl: 12, lg: 12, style: { fontWeight: "bold" } }}
                        wrapperCol={{ ...formItemLayout.wrapperCol, style: { direction: 'ltr' } }}
                        key={'ContactDateP'}
                        rules={[{ required: true, message: 'زمان ' + methodTypeTitle + ' اجباری است' }]}
                    >
                        {props.disabled !== true ? <DatePickerJalali
                            onChange={props.onChangeContactDate}
                            format="YYYY/MM/DD HH:mm"
                            showTime={true}
                            inputReadOnly={true}
                            style={{ width: '100%', marginBottom: 8, display: 'block' }}
                        /> : <span style={{ fontWeight: 'bold' }}>{dayjs(props.currentEntity.ContactDate).calendar('jalali').locale('fa').format("YYYY/MM/DD HH:mm")}</span>}
                    </Form.Item>
                } />
            </Col>

            {/*{evaluator && EvaluationMethod === enmEvaluationMethods.PolicyholderInPerson && props.showPolicyholderMessage === true &&*/}
            {/*    <Col xxl={18} xl={18} lg={24} md={24} sm={24} xs={24} >*/}
            {/*        <Alert type="info" style={{ marginTop: 10 }} message={*/}
            {/*            <p>*/}
            {/*                بیمه گزار/مشتری گرامی، شماره تماس و آدرس مرکز ارزیابی <b>{evaluator.FullName}</b> به شرح ذیل است. لطفاً در هنگام مراجعه به مرکز ارزیابی، مدارک زیر را جهت سهولت در روند ارزیابی به همراه داشته باشید :*/}
            {/*                1. مدارک شناسایی (کارت ملّی یا شناسنامه)*/}
            {/*                2. در حالاتی غیر از حالات سرقت کلی، سرقت با شکست حرز و سیل و طوفان ارائه دستگاه آسیب دیده الزامی است.*/}
            {/*                3. اصل سایر مدارک آپلود شده*/}
            {/*                شماره تماس مرکز ارزیابی : <b>{evaluator.Phone}</b>*/}
            {/*                آدرس مرکز ارزیابی: <b>{evaluator.Address}</b>*/}
            {/*                ساعت کار : <b>{evaluator.WorkTime}</b>*/}
            {/*            </p>*/}
            {/*        } />*/}
            {/*    </Col>*/}
            {/*}*/}

            {/*{evaluator && EvaluationMethod === enmEvaluationMethods.EvaluatorInPerson && props.showPolicyholderMessage === true &&*/}
            {/*    <Col xxl={18} xl={18} lg={24} md={24} sm={24} xs={24} >*/}
            {/*        <Alert type="info" style={{ marginTop: 10 }} message={*/}
            {/*            <p>*/}
            {/*                بیمه گزار/مشتری گرامی، لطفاً در هنگام مراجعه حضوری ارزیاب، مدارک زیر را جهت سهولت در روند ارزیابی به همراه داشته باشید :*/}
            {/*                1. مدارک شناسایی (کارت ملّی یا شناسنامه)*/}
            {/*                2. در حالاتی غیر از حالات سرقت کلی، سرقت با شکست حرز و سیل و طوفان ارائه دستگاه آسیب دیده الزامی است.*/}
            {/*                3. اصل سایر مدارک آپلود شده*/}
            {/*                ارزیاب: <b>{evaluator.FullName}</b>*/}
            {/*                شماره تماس مرکز ارزیابی : <b>{evaluator.Phone}</b>*/}
            {/*            </p>*/}
            {/*        } />*/}
            {/*    </Col>*/}
            {/*}*/}

            {props.disabled === true ? <></> :
                <Form.Item wrapperCol={{ dir: 'ltr' }} style={{ zIndex: 1000, borderTop: '1px solid', borderColor: '#e5e5e5', marginTop: 10 }}>
                    <Button type="primary" htmlType="submit" icon={<ArrowLeftOutlined />} loading={props.loading} style={{ width: "100px", marginTop: 10, marginBottom: 10 }}>
                        بعدی
                    </Button>

                    <Button type="ghost" htmlType="reset" icon={<StopOutlined />} loading={props.loading} style={{ width: "100px", marginTop: 10, marginBottom: 10, marginLeft: 10, background: '#fff' }}>
                        لغو
                    </Button>
                </Form.Item>
            }
        </Form>
    );
}

export class ContactSetting extends EvaluationComponent<IContactSetting, IContactSettingProps, IContactSettingStates>{
    onInitializing() {
        this.defaultEntity = {
            State: enmEntityState.Added,
            ConferenceUrl: this.props.damageCase.EvaluationMethod === enmEvaluationMethods.VideoCall
                ? "https://meet.jit.si/Zobdeh_" + this.props.damageCase.TicketNo.toString() + "_" + this.props.damageCase.Code.toString()
                : undefined
        } as any;

        const title = evaluationMethodsOptions.find(m => m.value === this.props.damageCase.EvaluationMethod)!.contactSettingCommandTitle;
        this.defaultState = {
            title,
            previousMakingContactAttachments: [],
            evaluationMethod: this.props.damageCase.EvaluationMethod,
            allDamageCases: []
        } as any;

        this.onInitialDataFetching = (params: any) => {
            params.damageCaseId = this.props.damageCase.Id;
            return params;
        };

        this.onInitialDataFetched = (result) => {
            let contactSettingInitialData = result.Data as IContactSettingInitialData;
            this.setState({
                loading: false,
                policyholderConfirmation: contactSettingInitialData.PolicyholderConfirmation,
                previousMakingContactAttachments: contactSettingInitialData.PreviousMakingContactAttachments,
                evaluationSteps: contactSettingInitialData.EvaluationSteps,
                damageCaseDocumentTypeAttachments: contactSettingInitialData.DamageCaseDocumentTypeAttachments,
                policyCert: contactSettingInitialData.PolicyCert,
                allDamageCases: contactSettingInitialData.AllDamageCases
            });
        };

    }
    onChangeDescription = (value?: any) => {
        let currentEntity = this.state.currentEntity;
        currentEntity.Description = value;
        this.setState({ currentEntity });
    }
    onChangeConferenceUrl = (value?: any) => {
        let currentEntity = this.state.currentEntity;
        currentEntity.ConferenceUrl = value;
        this.setState({ currentEntity });
    }
    onChangeContactDate = (e: any) => {
        let currentEntity = this.state.currentEntity;
        if (e) {
            let parsedDate = e.toDate();
            parsedDate = new Date(parsedDate.getTime() - parsedDate.getTimezoneOffset() * 60000);
            currentEntity.ContactDate = parsedDate;
        }
        else
            currentEntity.ContactDate = undefined;
        this.setState({ currentEntity });
    }
    onSubmit = async () => {
        if (!this.state.currentEntity)
            return;
        let currentEntity = this.state.currentEntity;
        let fetchOpt: ICallOptions = {
            url: "damagecaseevaluator/contactsetting",
            data: {
                evaluation: {
                    RowGuid: currentEntity.RowGuid,
                    DamageCaseEvaluatorId: currentEntity.DamageCaseEvaluatorId,
                    Description: currentEntity.Description,
                    ConferenceUrl: currentEntity.ConferenceUrl,
                    ContactDate: currentEntity.ContactDate
                }
            },
            onSuccess: (result: IResultSet<any>) => {
                if (result.ValidationState.Successful) {
                    Core.successNotification('تنظیم تماس انجام شد');
                    this.props.onClose && this.props.onClose(this);
                }
            }
        };
        return await this.callApi(fetchOpt);
    }
    renderBody() {
        return (
            <ContactSettingForm
                key="ContactSettingForm"
                loading={this.state.loading}
                currentEntity={this.state.currentEntity}
                damageCase={this.props.damageCase}
                onClose={this.props.onClose}
                onSubmit={this.onSubmit}
                previousMakingContactAttachments={this.state.previousMakingContactAttachments}
                policyholderConfirmation={this.state.policyholderConfirmation}
                onChangeContactDate={this.onChangeContactDate}
                onChangeDescription={this.onChangeDescription}
                onChangeConferenceUrl={this.onChangeConferenceUrl}
                damageCaseDocumentTypeAttachments={this.state.damageCaseDocumentTypeAttachments}
            />
        );
    }
}